import { Anchor, Collapse, Group, Stack, Text } from '@mantine/core';
import { IconClipboardList } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auditRouteFor, type EntityType } from '../../domain/entityTypes.js';
import { recordGroups } from '../../domain/entityDiff.js';
import { countWord } from '../../domain/pluralise.js';
import { ago, dateTime } from '../../utils/format.js';
import { DiffGroups, type DiffHeadings } from '../Diff/DiffGroups.js';

const FULL_RECORD: DiffHeadings = ['Field', 'Before', 'Value'];

export type MasterSummaryProps = {
    type: EntityType;
    entityKey: string;
    master: {
        targets: string;
        base: Record<string, unknown>;
        revision: number;
        updatedAt: string;
        overrides: unknown[];
    } | null;
    onClose: () => void;
};

/**
 * The master record, read-only: what the hub wants, before any environment is considered, and
 * its audit trail. Edit and Delete master live in the drawer's header.
 */
export const MasterSummary = ({ type, entityKey: key, master, onClose }: MasterSummaryProps) => {
    const [showFull, setShowFull] = useState(false);

    if (!master) {
        return (
            <Text fz="sm" c="dimmed">
                Not in master. Adopt it from an environment below.
            </Text>
        );
    }

    return (
        <Stack gap="sm">
            <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                    Targets
                </Text>
                <Text className="rgs-numeric" fz="sm">
                    {master.targets}
                </Text>
            </Stack>
            <Text fz="xs" c="dimmed">
                Revision <span className="rgs-numeric">{master.revision}</span> · saved {ago(master.updatedAt)} (
                {dateTime(master.updatedAt)})
                {master.overrides.length > 0 && ` · ${countWord(master.overrides.length, 'override')}`}
            </Text>
            <Anchor
                component="button"
                type="button"
                fz="xs"
                c="dimmed"
                aria-expanded={showFull}
                onClick={() => setShowFull((v) => !v)}
            >
                {showFull ? 'Hide full record' : 'Show full record'}
            </Anchor>
            <Collapse in={showFull}>
                <DiffGroups groups={recordGroups(type, master.base)} headings={FULL_RECORD} afterOnly />
            </Collapse>
            <Group gap="xs" wrap="wrap">
                <Anchor component={Link} to={auditRouteFor(type, key)} fz="sm" onClick={onClose}>
                    <Group gap={4} wrap="nowrap">
                        <IconClipboardList size={14} stroke={1.7} /> Audit log for this record →
                    </Group>
                </Anchor>
            </Group>
        </Stack>
    );
};
